import { Roles } from "../constants/app";
import { DocumentKind, StorageItem, StorageKind } from "../constants/document";
import routes from '../constants/routes.json';

const HIDDEN_FILE_NAME = '.hide';

/**
 * Walks the storage tree bottom-up, mutating each folder node to attach a
 * `documentCount` field with the recursive number of documents it contains
 * (excluding `.hide` marker files). Returns the same items reference for
 * chaining.
 */
export const annotateDocumentCounts = (items: StorageItem[]): StorageItem[] => {
  const countAndAnnotate = (node: StorageItem): number => {
    if (node.kind === StorageKind.document) {
      return node.text === HIDDEN_FILE_NAME ? 0 : 1;
    }
    const total = (node.items ?? []).reduce(
      (acc: number, child: StorageItem) => acc + countAndAnnotate(child),
      0,
    );
    node.documentCount = total;
    return total;
  };
  items.forEach(countAndAnnotate);
  return items;
};

/**
 * Multi-token substring match. The query is split into whitespace-separated
 * tokens; every token must appear in `text` as a case-insensitive substring.
 * Tokens can appear in any order across the text and do not need to be
 * adjacent. An empty / whitespace-only query trivially matches everything.
 *
 * This deliberately uses substring (not subsequence) per token: subsequence
 * matching is too permissive for short queries with common letters
 * (e.g. `sprint` finds s-p-r-i-n-t scattered through unrelated names like
 * `Stats_Red_Bull_Grand_Prix_of_the_United_States.pdf`). Substring matches
 * what users actually expect from a file search box.
 */
export const fuzzyMatch = (text: string, query: string): boolean => {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const lowerText = text.toLowerCase();
  return tokens.every((token) => lowerText.includes(token));
};

/**
 * Returns a new tree containing only the nodes whose `text` matches `query`
 * fuzzily (see `fuzzyMatch`) plus the ancestors needed to keep them
 * reachable. Folders that themselves match are kept along with their full
 * subtree intact. An empty / whitespace-only query returns the original
 * `items` reference unchanged.
 *
 * Filtered folder nodes carry an extra `matchedDocumentCount` field with the
 * recursive number of matched documents in their (filtered) subtree, so the
 * UI can render `matched/total`. The original `documentCount` is preserved
 * unchanged. The returned nodes are shallow copies; mutations on the result
 * do not affect the source tree.
 */
export const filterTreeByText = (items: StorageItem[], query: string): StorageItem[] => {
  if (!query.trim()) return items;

  const filterNode = (node: StorageItem): StorageItem | null => {
    const matches = fuzzyMatch(node.text, query);

    if (node.kind === StorageKind.document) {
      return matches && node.text !== HIDDEN_FILE_NAME ? node : null;
    }

    if (matches) {
      // Folder name matches: keep the entire subtree intact and report all
      // of its documents as matches.
      return { ...node, matchedDocumentCount: node.documentCount ?? 0 };
    }

    const filteredChildren = (node.items ?? [])
      .map(filterNode)
      .filter((c): c is StorageItem => c !== null);

    if (filteredChildren.length === 0) return null;

    const matchedDocumentCount = filteredChildren.reduce((acc: number, child: StorageItem) => {
      if (child.kind === StorageKind.document) return acc + 1;
      return acc + (child.matchedDocumentCount ?? 0);
    }, 0);

    return { ...node, items: filteredChildren, matchedDocumentCount };
  };

  return items
    .map(filterNode)
    .filter((c): c is StorageItem => c !== null);
};

/**
 * Forces the browser to download `url` as a file named `fileName`, even
 * for content types it would normally render inline (e.g. PDFs, images).
 * Fetches the resource as a blob via XHR and triggers a synthetic anchor
 * click. The XHR + blob detour is what makes the `download` attribute
 * actually fire for cross-content-type cases that would otherwise just
 * navigate the tab.
 *
 * Returns a Promise so callers can `await` and catch network / CORS / HTTP
 * errors instead of letting them die silently inside `xhr.onload`.
 */
export const forceDownload = (url: string, fileName: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.onload = function () {
      if (this.status < 200 || this.status >= 300) {
        reject(new Error(`HTTP ${this.status}`));
        return;
      }
      try {
        const urlCreator = window.URL || (window as any).webkitURL;
        const blobUrl = urlCreator.createObjectURL(this.response);
        const tag = document.createElement('a');
        tag.href = blobUrl;
        tag.download = fileName;
        document.body.appendChild(tag);
        tag.click();
        document.body.removeChild(tag);
        // Release the blob URL on the next tick so the browser has a chance
        // to start the download before we revoke it.
        setTimeout(() => urlCreator.revokeObjectURL(blobUrl), 0);
        resolve();
      } catch (e) {
        reject(e);
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.onabort = () => reject(new Error('Download aborted'));
    xhr.send();
  });

/**
 * Walks the storage tree and returns the `text` of every folder node, in
 * pre-order. Used to build the auto-expand list while a tree filter is
 * active so users see every match without having to expand folders manually.
 */
export const collectFolderTexts = (items: StorageItem[]): string[] => {
  const result: string[] = [];
  const walk = (node: StorageItem) => {
    if (node.kind === StorageKind.folder) {
      result.push(node.text);
      (node.items ?? []).forEach(walk);
    }
  };
  items.forEach(walk);
  return result;
};

export const getVersion = (): string => `${process.env.REACT_APP_VERSION}`;

export const getRedirectionUrl = (role: Roles) => {
  switch (role) {
    case Roles.Admin:
      return routes['documents'];
    default:
      return routes['documents'];
  }
};

export const getDocumentKindByExtension= (fileName:string): DocumentKind => {
  const is = (fileName:string, ext:string) => new RegExp(`.${ext}`).test(fileName);

  if (is(fileName, "pdf")) {
    return DocumentKind.pdf;
  } else if (is(fileName, "jpg|png|jpeg")) {
    return DocumentKind.image;
  } else if (is(fileName, "doc|docx|xls|xlsx|odt")) {
    return DocumentKind.office;
  } else {
    return DocumentKind.unknown;
  }
}

export const getDocumentKindByContentType = (contentType:string|undefined): DocumentKind => {
  switch(contentType) {
    case "image/png":
    case "image/jpeg":
    case "image/jpg":
    case "image/gif":
    case "image/bmp":
    case "image/svg+xml":
        return DocumentKind.image;
    case "application/pdf":
      return DocumentKind.pdf;
    case "application/msword":
    case "application/vnd.ms-powerpoint":
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return DocumentKind.office;
    default:
      return DocumentKind.unknown;      
  }
}
